import { useState, useEffect, useCallback } from "react";
import Marquee from "react-fast-marquee";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Star, Waveform, Timer } from "@phosphor-icons/react";

const AutoplayCountdown = ({ advanceAt, totalSeconds, clockOffset }) => {
  const [remaining, setRemaining] = useState(totalSeconds || 0);
  useEffect(() => {
    if (!advanceAt) return;
    const target = new Date(advanceAt).getTime();
    const tick = () => {
      const nowServer = Date.now() - clockOffset;
      setRemaining(Math.max(0, Math.round((target - nowServer) / 1000)));
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [advanceAt, clockOffset]);

  const pct = totalSeconds ? Math.max(0, Math.min(100, (remaining / totalSeconds) * 100)) : 0;
  const label = remaining >= 60 ? `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, "0")}` : `${remaining}s`;
  return (
    <div className="flex items-center gap-3 shrink-0" data-testid="autoplay-countdown">
      <Timer size={16} className="text-[#E1FF00]" />
      <div className="w-24 h-1.5 bg-white/10 overflow-hidden rounded-full">
        <div className="h-full bg-[#E1FF00] transition-[width] duration-500 ease-linear" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-xs text-[#A1A1AA] w-12 text-right">{label}</span>
    </div>
  );
};

const StarRow = ({ value, onChange, size = 24, readOnly = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          data-testid={readOnly ? undefined : `rating-star-${n}`}
          onMouseEnter={() => !readOnly && setHover(n)}
          onMouseLeave={() => !readOnly && setHover(0)}
          onClick={() => !readOnly && onChange(n)}
          className={readOnly ? "" : "cursor-pointer"}
        >
          <Star size={size} weight={(hover || value) >= n ? "fill" : "regular"} className={(hover || value) >= n ? "text-[#E1FF00]" : "text-[#52525B]"} />
        </button>
      ))}
    </div>
  );
};

export const PlayerStage = ({ track, onNext, refreshKey, advanceAt, reviewSeconds, clockOffset }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [critique, setCritique] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadReviews = useCallback(async () => {
    if (!track) { setReviews([]); return; }
    try {
      const res = await api.get(`/tracks/${track.id}`);
      setReviews(res.data.reviews || []);
    } catch (e) {
      console.error("Failed to load reviews", e);
    }
  }, [track]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews, refreshKey]);

  const submitReview = async () => {
    if (!user) return toast.error("Sign in to leave a critique");
    if (!rating) return toast.error("Pick a star rating");
    setSubmitting(true);
    try {
      await api.post(`/tracks/${track.id}/review`, { rating, critique });
      toast.success("Critique posted");
      setRating(0); setCritique("");
      loadReviews();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post critique");
    } finally {
      setSubmitting(false);
    }
  };

  const isOwner = user && track && track.user_id === user.user_id;
  const totalSeconds = track?.played_at && advanceAt
    ? Math.round((new Date(advanceAt).getTime() - new Date(track.played_at).getTime()) / 1000)
    : reviewSeconds;

  const reportDuration = (seconds) => {
    if (!track || !seconds || seconds <= 0) return;
    api.post(`/tracks/${track.id}/duration`, { seconds }).catch((e) => console.error("Duration report failed", e));
  };

  const renderPlayer = () => (
    <div className="bg-[#050505] border border-white/10 p-6 flex items-center gap-5">
      <div className="flex items-end gap-1 h-12 shrink-0">
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="eq-bar h-full" style={{ animationDelay: `${i * 0.1}s` }} />
        ))}
      </div>
      <audio
        data-testid="audio-player"
        controls
        autoPlay
        onLoadedMetadata={(e) => reportDuration(e.target.duration)}
        onEnded={() => onNext(track.id)}
        src={`${API}/audio/${track.id}`}
        className="w-full"
      />
    </div>
  );

  return (
    <div className="bg-[#0F0F13] border border-white/5 rounded-2xl overflow-hidden shadow-[0_0_40px_rgba(177,74,237,0.12)]">
      {/* Now playing bar */}
      <div className="bg-[#050505] border-b border-white/10 flex items-center">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[#FF3B30]/10 border-r border-white/10 shrink-0">
          <span className="w-2 h-2 rounded-full bg-[#FF3B30] animate-pulse" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#FF3B30] font-bold">On Air</span>
        </div>
        <Marquee speed={35} gradient={false} className="py-2.5">
          <span className="font-head font-bold text-sm uppercase tracking-wide text-[#A1A1AA] mx-6">
            {track ? `Now Playing — ${track.title} by ${track.artist_name}` : "Queue is empty — be the first to drop a track"} <span className="text-[#E1FF00] mx-4">●</span>
          </span>
        </Marquee>
        {track && advanceAt && (
          <div className="px-4 border-l border-white/10 shrink-0">
            <AutoplayCountdown advanceAt={advanceAt} totalSeconds={totalSeconds} clockOffset={clockOffset} />
          </div>
        )}
      </div>

      <div className="p-6 md:p-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={track?.id || "empty"}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {track ? (
              <>
                <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                  <div>
                    {track.priority && (
                      <span className="inline-block font-mono text-[10px] uppercase tracking-widest bg-[#E1FF00] text-black px-2 py-0.5 mb-2 font-bold">Priority Play</span>
                    )}
                    <h2 className="font-head font-black text-3xl md:text-4xl tracking-tighter leading-none">{track.title}</h2>
                    <p className="text-[#A1A1AA] mt-2">{track.artist_name} · <span className="text-[#B14AED] font-semibold">{track.genre}</span></p>
                  </div>
                  <div className="flex items-center gap-3 text-right">
                    <div>
                      <div className="font-head font-black text-2xl text-[#E1FF00] flex items-center gap-1 justify-end">
                        <Star size={20} weight="fill" /> {track.avg_rating || "—"}
                      </div>
                      <div className="font-mono text-xs text-[#52525B]">{track.rating_count} reviews</div>
                    </div>
                  </div>
                </div>

                {/* Player */}
                <div className="mb-6">
                  {renderPlayer()}
                </div>

                {/* Review form */}
                <div className="border-t border-white/10 pt-6">
                  <h3 className="font-head font-bold text-lg tracking-tight mb-4 flex items-center gap-2">
                    <Waveform size={20} className="text-[#0038FF]" /> Drop Your Critique
                  </h3>
                  {isOwner ? (
                    <p className="text-[#52525B] text-sm italic">This is your track — sit back and watch the room react.</p>
                  ) : (
                    <div className="space-y-4">
                      <StarRow value={rating} onChange={setRating} />
                      <Textarea
                        data-testid="critique-input"
                        value={critique}
                        onChange={(e) => setCritique(e.target.value)}
                        placeholder="Constructive feedback — what's working, what to tighten up…"
                        className="bg-[#050505] border-white/10 text-white rounded-none focus-visible:ring-2 focus-visible:ring-[#E1FF00] focus-visible:ring-offset-0 min-h-[90px]"
                      />
                      <Button
                        data-testid="submit-critique-button"
                        onClick={submitReview}
                        disabled={submitting}
                        className="bg-[#0038FF] hover:bg-[#002EDD] text-white font-bold rounded-none disabled:opacity-50"
                      >
                        {submitting ? "Posting…" : "Post Critique"}
                      </Button>
                    </div>
                  )}

                  {/* Reviews list */}
                  <div className="mt-6 space-y-3" data-testid="reviews-list">
                    {reviews.map((r) => (
                      <div key={r.id} className="bg-[#050505] border border-white/5 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Avatar className="h-7 w-7">
                              <AvatarImage src={r.reviewer_picture} />
                              <AvatarFallback className="bg-[#B14AED] text-white text-[10px]">{r.reviewer_name?.[0]?.toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <span className="font-semibold text-sm">{r.reviewer_name}</span>
                          </div>
                          <StarRow value={r.rating} readOnly size={14} />
                        </div>
                        {r.critique && <p className="text-[#A1A1AA] text-sm leading-relaxed">{r.critique}</p>}
                      </div>
                    ))}
                    {reviews.length === 0 && <p className="text-[#52525B] text-sm">No critiques yet. Be the first.</p>}
                  </div>
                </div>
              </>
            ) : (
              <div className="py-20 text-center">
                <Waveform size={48} className="text-[#52525B] mx-auto mb-4" />
                <p className="font-head font-bold text-xl tracking-tight mb-1">The stage is quiet</p>
                <p className="text-[#A1A1AA]">Submit a track to kick off the show.</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
